import Checkbox from "@material-ui/core/Checkbox";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import FormControl, { FormControlProps } from "@material-ui/core/FormControl";
import ListItemText, { ListItemTextProps } from "@material-ui/core/ListItemText";
import MUISelect, { SelectProps as MUISelectProps } from "@material-ui/core/Select";
import React, { FC, useState } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { FixedSizeList as List } from "react-window";
import { styled } from "@material-ui/styles";

export type SelectOptionType = {
  label: string;
  value: string;
  key?: string | number;
};

type SelectProps = {
  options: SelectOptionType[];
};

const StyledFormControl = styled(FormControl)<FormControlProps>(() => ({
  "& .MuiInputBase-root": {
    height: 48,
  },
  "& label": {
    transform: "translate(14px, 17px) scale(1)",
  },
  "& label.Mui-focused": {
    color: "#034980",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "1px solid #9E9E9E",
      borderRadius: 4,
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #034980",
    },
  },
}));

const StyledListItemText = styled(ListItemText)<ListItemTextProps>(() => ({
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
}));

const SelectWithCheckboxes: FC<MUISelectProps & FormControlProps & SelectProps> = ({
  variant,
  className,
  label,
  value: propValue,
  onChange: propOnChange,
  name,
  options,
  ...rest
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>((propValue as string[]) || []);
  const [searchTerm, setSearchTerm] = useState("");

  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
  const filteredOptions = sortedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleChange = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    setSelectedValues(newSelectedValues);

    if (propOnChange) {
      const event = {
        target: { value: newSelectedValues, name },
      } as unknown as React.ChangeEvent<{ value: unknown }>;

      propOnChange(event, null);
    }
  };

  return (
    <StyledFormControl variant={variant ?? "outlined"} className={className} fullWidth>
      <InputLabel>{label}</InputLabel>
      <MUISelect
        value={selectedValues}
        renderValue={(selected) =>
          options
            .filter((x) => (selected as string[]).includes(x.value))
            .map((x) => x.label)
            ?.join(", ")
        }
        name={name}
        label={label}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        }}
        multiple
        {...rest}
      >
        {!options || options.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height={120}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <div>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ margin: "8px" }}
            />
            <List
              height={filteredOptions.length > 20 ? 500 : filteredOptions.length * 48}
              itemCount={filteredOptions.length}
              itemSize={48}
              width="100%"
            >
              {({ index, style }: any) => {
                const option = filteredOptions[index];
                return (
                  <MenuItem
                    key={option.key || option.label}
                    value={option.value}
                    style={style}
                    onClick={() => handleChange(option.value)}
                  >
                    <Checkbox checked={selectedValues.includes(option.value)} color="primary" />
                    <StyledListItemText primary={option.label} />
                  </MenuItem>
                );
              }}
            </List>
          </div>
        )}
      </MUISelect>
    </StyledFormControl>
  );
};

export default SelectWithCheckboxes;
